import React, { useState, useEffect } from 'react';
import './AdminMessages.css';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });

  // 根据当前访问地址自动判断 API 地址
  const API_BASE = process.env.REACT_APP_API_BASE || 
    (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  // 获取留言列表
  const fetchMessages = async (page = 1) => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/admin/messages?page=${page}&page_size=${pagination.pageSize}`;
      if (activeTab !== 'all') {
        url += `&status=${activeTab}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.items || []);
        setPagination(prev => ({ ...prev, page, total: data.total }));
      }
    } catch (err) {
      console.error('获取留言失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(1);
  }, [activeTab]);

  // 回复留言
  const handleReply = async () => {
    if (!replyContent.trim()) {
      alert('请输入回复内容');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/messages/${selectedMessage.id}/reply`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reply_content: replyContent }),
      });

      if (response.ok) {
        setSelectedMessage(null);
        setReplyContent('');
        fetchMessages(pagination.page);
        alert('回复成功！');
      } else {
        const error = await response.json();
        alert(error.detail || '回复失败');
      }
    } catch (err) {
      alert('回复失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 更新状态
  const handleUpdateStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/messages/${id}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        fetchMessages(pagination.page);
      } else {
        const error = await response.json();
        alert(error.detail || '操作失败');
      }
    } catch (err) {
      alert('操作失败');
    }
  };

  // 删除留言
  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条留言吗？')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/messages/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        fetchMessages(pagination.page);
      } else {
        const error = await response.json();
        alert(error.detail || '删除失败');
      }
    } catch (err) {
      alert('删除失败');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: '待回复', class: 'status-pending' },
      replied: { label: '已回复', class: 'status-replied' },
      closed: { label: '已关闭', class: 'status-closed' },
    };
    const s = statusMap[status] || statusMap.pending;
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  return (
    <div className="admin-messages-page">
      <div className="page-header">
        <h1>留言管理</h1>
        <p>查看和回复用户留言</p>
      </div>

      {/* 标签筛选 */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            全部
          </button>
          <button 
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            待回复
          </button>
          <button 
            className={`tab ${activeTab === 'replied' ? 'active' : ''}`}
            onClick={() => setActiveTab('replied')}
          >
            已回复
          </button>
          <button 
            className={`tab ${activeTab === 'closed' ? 'active' : ''}`}
            onClick={() => setActiveTab('closed')}
          >
            已关闭
          </button>
        </div>
      </div>

      {/* 留言列表 */}
      <div className="messages-table-container">
        {loading ? (
          <div className="loading">加载中...</div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>暂无留言</p>
          </div>
        ) : (
          <table className="messages-table">
            <thead>
              <tr>
                <th>用户</th>
                <th>主题</th>
                <th>状态</th>
                <th>提交时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((msg) => (
                <tr key={msg.id}>
                  <td>{msg.username || 'Unknown'}</td>
                  <td className="subject-cell">
                    <div className="subject-text" onClick={() => setSelectedMessage(msg)}>
                      {msg.subject}
                    </div>
                  </td>
                  <td>{getStatusBadge(msg.status)}</td>
                  <td>{formatDate(msg.created_at)}</td>
                  <td>
                    <div className="action-btns">
                      <button className="view-btn" onClick={() => setSelectedMessage(msg)}>
                        查看
                      </button>
                      {msg.status === 'pending' && (
                        <button className="reply-btn" onClick={() => setSelectedMessage(msg)}>
                          回复
                        </button>
                      )}
                      {msg.status === 'replied' && (
                        <button className="close-btn" onClick={() => handleUpdateStatus(msg.id, 'closed')}>
                          关闭
                        </button>
                      )}
                      {msg.status === 'closed' && (
                        <button className="reopen-btn" onClick={() => handleUpdateStatus(msg.id, 'pending')}>
                          重新打开
                        </button>
                      )}
                      <button className="delete-btn" onClick={() => handleDelete(msg.id)}>
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* 分页 */}
        {pagination.total > pagination.pageSize && (
          <div className="pagination">
            <button 
              disabled={pagination.page === 1}
              onClick={() => fetchMessages(pagination.page - 1)}
            >
              上一页
            </button>
            <span>第 {pagination.page} 页，共 {Math.ceil(pagination.total / pagination.pageSize)} 页</span>
            <button 
              disabled={pagination.page * pagination.pageSize >= pagination.total}
              onClick={() => fetchMessages(pagination.page + 1)}
            >
              下一页
            </button>
          </div>
        )}
      </div>

      {/* 留言详情弹窗 */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>留言详情</h2>
              <button className="close-btn" onClick={() => setSelectedMessage(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-row">
                  <label>用户：</label>
                  <span>{selectedMessage.username}</span>
                </div>
                <div className="detail-row">
                  <label>邮箱：</label>
                  <span>{selectedMessage.user_email || '-'}</span>
                </div>
                <div className="detail-row">
                  <label>状态：</label>
                  {getStatusBadge(selectedMessage.status)}
                </div>
                <div className="detail-row">
                  <label>提交时间：</label>
                  <span>{formatDate(selectedMessage.created_at)}</span>
                </div>
              </div>

              <div className="detail-section">
                <label>主题</label>
                <div className="section-content">{selectedMessage.subject}</div>
              </div>

              <div className="detail-section">
                <label>内容</label>
                <div className="section-content pre-wrap">{selectedMessage.content}</div>
              </div>

              {selectedMessage.reply_content && (
                <div className="detail-section reply-section">
                  <label>管理员回复</label>
                  <div className="reply-info">
                    回复时间：{formatDate(selectedMessage.replied_at)}
                  </div>
                  <div className="section-content pre-wrap">{selectedMessage.reply_content}</div>
                </div>
              )}

              {selectedMessage.status === 'pending' && (
                <div className="detail-section reply-form">
                  <label>回复内容</label>
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="请输入回复内容..."
                    rows={5}
                  />
                  <button 
                    className="submit-reply-btn" 
                    onClick={handleReply}
                    disabled={submitting}
                  >
                    {submitting ? '提交中...' : '提交回复'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
